import { Tab, TabList, TabPanel, TabPanels, Tabs } from '@reach/tabs'
import { clsx } from 'clsx'
import * as React from 'react'
import {
	Link,
	Outlet,
	useNavigate,
	data as json,
	type HeadersFunction,
	type MetaFunction,
} from 'react-router'
import { ButtonLink } from '#app/components/button.tsx'
import { Grid } from '#app/components/grid.tsx'
import { ChevronDownIcon, ChevronUpIcon } from '#app/components/icons.tsx'
import { PodcastSubs } from '#app/components/podcast-subs.tsx'
import { BlogSection } from '#app/components/sections/blog-section.tsx'
import { HeroSection } from '#app/components/sections/hero-section.tsx'
import { Spacer } from '#app/components/spacer.tsx'
import { H4, H6, Paragraph } from '#app/components/typography.tsx'
import { externalLinks } from '#app/external-links.tsx'
import {
	getImageBuilder,
	getImgProps,
	images,
} from '#app/images.tsx'
import { type RootLoaderType } from '#app/root.tsx'
import { type CallKentSeason, type KCDHandle } from '#app/types.ts'
import { getBlogRecommendations } from '#app/utils/blog.server.ts'
import { groupBy } from '#app/utils/cjs/lodash.ts'
import {
	reuseUsefulLoaderHeaders,
} from '#app/utils/misc.ts'
import {
	CallsEpisodeUIStateProvider,
	useMatchLoaderData,
} from '#app/utils/providers.tsx'
import { type SerializeFrom } from '#app/utils/serialize-from.ts'
import { getServerTimeHeader } from '#app/utils/timing.server.ts'
import { getEpisodes } from '#app/utils/transistor.server.ts'
import { type Route } from './+types/_layout'

export const handle: KCDHandle & { id: string } = {
	id: 'calls',
}

export const getEpisodesBySeason = (
	episodes: Awaited<ReturnType<typeof getEpisodes>>,
) => {
	const groupedEpisodeBySeasons = groupBy(episodes, 'seasonNumber')
	const seasons: Array<CallKentSeason> = []
	Object.entries(groupedEpisodeBySeasons).forEach(([key, value]) => {
		seasons.push({
			seasonNumber: +key,
			episodes: value,
		})
	})
	return seasons
}

export async function loader({ request }: Route.LoaderArgs) {
	const timings = {}
	const [blogRecommendations, episodes] = await Promise.all([
		getBlogRecommendations({ request, timings }),
		getEpisodes({ request, timings }),
	])
	const socialMetas = (
		await import('#app/og/page-meta.server.ts')
	).buildPageSocialMetasForRequest(request, {
		title: 'Call Kent Podcast',
		description: `Leave Kent an audio message here, then your message and Kent's response are published in the podcast.`,
		keywords: 'podcast, call kent, call kent c. dodds, the call kent podcast',
		socialImage: {
			kind: 'generic-social',
			words: 'Listen to the Call Kent Podcast and make your own call.',
			featuredImage: images.microphone({
				fit: 'pad',
				width: 1200,
				height: 1200,
			}),
		},
	})

	return json(
		{ blogRecommendations, episodes, socialMetas },
		{
			headers: {
				'Cache-Control': 'private, max-age=3600',
				Vary: 'Cookie',
				'Server-Timing': getServerTimeHeader(timings),
			},
		},
	)
}

export const headers: HeadersFunction = reuseUsefulLoaderHeaders

export const meta: MetaFunction<typeof loader> = ({ data }) =>
	data?.socialMetas ?? []

export default function CallHomeScreen({
	loaderData: data,
	matches,
}: Route.ComponentProps) {
	const [sortOrder, setSortOrder] = React.useState<'desc' | 'asc'>('desc')

	const navigate = useNavigate()

	const groupedEpisodeBySeasons = groupBy(data.episodes, 'seasonNumber')
	const seasons: Array<CallKentSeason> = []
	Object.entries(groupedEpisodeBySeasons).forEach(([key, value]) => {
		seasons.push({
			seasonNumber: +key,
			episodes: value,
		})
	})

	//show latest season first.
	seasons.reverse()

	const last = matches[matches.length - 1]

	const seasonNumber = last?.params.season
		? Number(last.params.season)
		: // we use the first one because the seasons are in reverse order
			// oh, and this should never happen anyway because we redirect
			// in the event there's no season param. But it's just to be safe.
			(seasons[0]?.seasonNumber ?? 1)

	const currentSeason = seasons.find((s) => s.seasonNumber === seasonNumber)
	const tabIndex = currentSeason ? seasons.indexOf(currentSeason) : 0

	function handleTabChange(index: number) {
		const chosenSeason = seasons[index]
		if (chosenSeason) {
			void navigate(String(chosenSeason.seasonNumber).padStart(2, '0'), {
				preventScrollReset: true,
			})
		}
	}

	return (
		<>
			<HeroSection
				title="Calls with Kent C. Dodds."
				subtitle="You call, I'll answer."
				imageBuilder={images.microphone}
				arrowUrl="#episodes"
				arrowLabel="Take a listen"
				action={
					<ButtonLink variant="primary" to="./record" className="mr-auto">
						Record your call
					</ButtonLink>
				}
			/>

			<Grid>
				<H6 as="div" className="col-span-full mb-6">
					Listen to the podcasts here
				</H6>

				<PodcastSubs
					apple={externalLinks.callKentApple}
					pocketCasts={externalLinks.callKentPocketCasts}
					spotify={externalLinks.callKentSpotify}
					rss={externalLinks.callKentRSS}
				/>
			</Grid>

			<Spacer size="base" />

			<Grid>
				<div className="col-span-full lg:col-span-6">
					<img
						title="Photo by Luke Southern"
						{...getImgProps(
							getImageBuilder(
								'unsplash/photo-1571079570759-8b8800f7c412',
								'Phone sitting on a stool',
							),
							{
								className: 'w-full rounded-lg object-cover',
								widths: [512, 650, 840, 1024, 1300, 1680, 2000, 2520],
								sizes: [
									'(max-width: 1023px) 80vw',
									'(min-width: 1024px) and (max-width: 1620px) 40vw',
									'630px',
								],
								transformations: {
									fit: 'cover',
									aspectRatio: '4:3',
								},
							},
						)}
					/>
				</div>
				<Spacer size="xs" className="col-span-full block lg:hidden" />
				<div className="col-span-full lg:col-span-5 lg:col-start-8">
					<H4 as="p">{`What's this all about?`}</H4>
					<div className="flex flex-col gap-3">
						<Paragraph>
							{`The goal of the Call Kent Podcast is to `}
							<strong>get my answers to your questions.</strong>
							{`
              You record your brief question (120 seconds or less) right from
              your browser. Then I listen to it later and give my response,
              and through the magic of technology (ffmpeg), our question
              and answer are stitched together and published to the podcast
              feed.
            `}
						</Paragraph>
						<Paragraph>
							{`If recording isn't an option, you can also type your question and we'll generate the audio for you.`}
						</Paragraph>
						<Paragraph>{`I look forward to hearing from you!`}</Paragraph>
						<Spacer size="2xs" />
						<ButtonLink variant="primary" to="./record">
							Record your call
						</ButtonLink>
					</div>
				</div>
			</Grid>

			<Spacer size="base" />

			<Tabs
				as={Grid}
				className="mb-24 lg:mb-64"
				index={tabIndex}
				onChange={handleTabChange}
			>
				<TabList className="col-span-full mb-20 flex flex-col items-start bg-transparent lg:flex-row lg:flex-nowrap lg:gap-x-12 lg:overflow-x-auto lg:overscroll-x-contain lg:pb-1">
					{seasons.map((season) => (
						<Tab
							key={season.seasonNumber}
							tabIndex={-1}
							className="border-none p-0 text-left text-4xl leading-tight focus:bg-transparent focus:outline-none lg:shrink-0"
						>
							<Link
								preventScrollReset
								className={clsx(
									'hover:text-primary focus:text-primary whitespace-nowrap focus:outline-none',
									{
										'text-primary': season.seasonNumber === seasonNumber,
										'text-slate-500': season.seasonNumber !== seasonNumber,
									},
								)}
								to={String(season.seasonNumber).padStart(2, '0')}
								onClick={(e) => {
									if (e.metaKey) {
										e.stopPropagation()
									} else {
										e.preventDefault()
									}
								}}
							>
								{`Season ${season.seasonNumber}`}
							</Link>
						</Tab>
					))}
				</TabList>

				{currentSeason ? (
					<div className="col-span-full mb-6 flex flex-col lg:mb-12 lg:flex-row lg:justify-between">
						<H6
							id="episodes"
							as="h2"
							className="col-span-full mb-10 flex flex-col lg:mb-0 lg:flex-row"
						>
							<span>Calls with Kent C. Dodds</span>
							&nbsp;
							<span>{`Season ${currentSeason.seasonNumber} — ${currentSeason.episodes.length} episodes`}</span>
						</H6>

						<button
							className="text-primary group relative text-lg font-medium focus:outline-none"
							onClick={() =>
								setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'))
							}
						>
							<div className="bg-secondary absolute -top-2 -right-4 -bottom-2 -left-4 rounded-lg opacity-0 transition group-hover:opacity-100 group-focus:opacity-100" />
							<span className="relative inline-flex items-center">
								{sortOrder === 'asc' ? (
									<>
										Showing oldest first
										<ChevronUpIcon className="ml-2 text-gray-400" />
									</>
								) : (
									<>
										Showing newest first
										<ChevronDownIcon className="ml-2 text-gray-400" />
									</>
								)}
							</span>
						</button>
					</div>
				) : null}

				<TabPanels className="col-span-full">
					{seasons.map((season) => (
						<TabPanel
							key={season.seasonNumber}
							className="border-t border-gray-200 focus:outline-none dark:border-gray-600"
						>
							<CallsEpisodeUIStateProvider value={{ sortOrder }}>
								<Outlet />
							</CallsEpisodeUIStateProvider>
						</TabPanel>
					))}
				</TabPanels>
			</Tabs>

			{seasons.length === 0 ? (
				<Grid className="mb-24">
					<div className="col-span-full rounded-lg border border-gray-200 p-8 dark:border-gray-600">
						<H4 as="h2" className="mb-3">
							No calls are available right now.
						</H4>
						<Paragraph className="mb-4">
							We are likely having trouble with our Transistor integration.
							Please try again soon, or listen directly on the{' '}
							<a
								href={externalLinks.callKentRSS}
								target="_blank"
								rel="noreferrer noopener"
								className="text-primary underline"
							>
								Transistor feed
							</a>
							.
						</Paragraph>
						<ButtonLink variant="primary" to="./record">
							Record your call
						</ButtonLink>
					</div>
				</Grid>
			) : null}

			<BlogSection
				articles={data.blogRecommendations}
				title="Looking for more content?"
				description="Have a look at these articles."
			/>
		</>
	)
}

export const useCallsData = () =>
	useMatchLoaderData<SerializeFrom<typeof loader>>(handle.id)
